import type { TenantCurrentResponse } from "@/services/authService";
import type { CertificateRequestStatus } from "@/services/certificateRequestsService";
import type { OwnerPostCertViewSteps } from "@/pages/onboarding/ownerPostCertSteps";
import type { OnboardingStepContext } from "@/pages/onboarding/stepMachine/onboardingStepContext";
import { readDgiStep3ContinuedFromSession } from "@/pages/onboarding/dgi-onboarding-session";
import { isHomologacionComplete } from "@/pages/onboarding/homologacionStatus";

export interface BuildStepContextArgs {
  loading: boolean;
  tenant: TenantCurrentResponse | null;
  userRole: string | undefined;
  isAccountantFlowOwner: boolean;
  isAccountantUser: boolean;
  isAlreadyIssuer: boolean;
  certBeforeDgi: boolean;
  certificateAcquisitionChoice: "myself" | "fiscalo" | null;
  isFiscalProfileSettled: boolean;
  certificatePath: string | null;
  /** currentCertificateRequest?.status ?? null. */
  certificateRequestStatus: CertificateRequestStatus | null;
  hasAccountantMember: boolean;
  dgiStepsCompletedConfirmed: boolean;
  dgiStep3SuccessToastAcknowledged: boolean;
  caeListLoading: boolean;
  hasRequiredOnboardingCaes: boolean;
  needsCfeImportStep: boolean;
  ownerPostCertViewSteps: OwnerPostCertViewSteps;
  totalSteps: number;
  /** 2 for the self path (régimen + plan precede situation), 0 otherwise. */
  preCertOffset: number;
  wasLoading: boolean;
  prevViewStep: number;
}

/**
 * Map the hook's in-scope values (plus sessionStorage reads) into the pure
 * `OnboardingStepContext` that `resolveStep` consumes. The derived-field
 * formulas here mirror the current step-sync effect exactly (s1Complete's
 * precedence, s3Complete, autoFlowPortalUserStamped, etc.).
 */
export function buildStepContext(
  args: BuildStepContextArgs,
): OnboardingStepContext {
  const t = args.tenant;
  const id = t?.id ?? null;

  const autoFlowPortalUserStamped = Boolean(
    (t?.dgiAutoOnboardingProgress as Record<string, unknown> | null)
      ?.portal_user,
  );
  // NB: same operator precedence as the source — (A && B && C && D && E) || F.
  const s1Complete =
    ((t?.address?.trim() ?? "") !== "" &&
      (t?.city?.trim() ?? "") !== "" &&
      (t?.department?.trim() ?? "") !== "" &&
      (t?.phone?.trim() ?? "") !== "" &&
      (t?.industry?.trim() ?? "") !== "") ||
    (t?.profession?.trim() ?? "") !== "";
  const literalEComplete = t?.literalE !== undefined && t?.literalE !== null;
  const s3Complete = Boolean(
    (t?.legalName?.trim() ?? "") !== "" || t?.profileImage || t?.invoiceLogoImage,
  );
  const hasLogo = Boolean(t?.profileImage || t?.invoiceLogoImage);

  return {
    loading: args.loading,
    hasTenant: t != null,
    onboardingMode: t?.onboardingMode ?? null,
    onboardingSituationChoice: t?.onboardingSituationChoice ?? null,
    onboardingCompletedAt: t?.onboardingCompletedAt ?? null,
    planId: t?.planId ?? null,
    fiscalBenefitDeclared: Boolean(t?.fiscalBenefitDeclared),
    hasDgiPortalUser: Boolean(t?.hasDgiPortalUser),
    dgiPortalUserConfirmed: Boolean(t?.dgiPortalUserConfirmed),
    dgiPortalUserConfirmedAt: Boolean(t?.dgiPortalUserConfirmedAt),
    homologacionCompleted: isHomologacionComplete(
      t?.homologacionStatus,
      Boolean(t?.produccionRoleRequired),
    ),
    autoFlowPortalUserStamped,
    hasDgiCredentials: Boolean(t?.hasDgiCredentials),
    dgiCertificateSkippedAt: Boolean(t?.dgiCertificateSkippedAt),
    s1Complete,
    literalEComplete,
    s3Complete,
    hasLogo,
    onboardingCfeImportStepCompletedAt: Boolean(
      t?.onboardingCfeImportStepCompletedAt,
    ),
    userRole: args.userRole,
    isAccountantFlowOwner: args.isAccountantFlowOwner,
    isAccountantUser: args.isAccountantUser,
    isAlreadyIssuer: args.isAlreadyIssuer,
    certBeforeDgi: args.certBeforeDgi,
    certificateAcquisitionChoice: args.certificateAcquisitionChoice,
    isFiscalProfileSettled: args.isFiscalProfileSettled,
    hasAccountantMember: args.hasAccountantMember,
    dgiStepsCompletedConfirmed: args.dgiStepsCompletedConfirmed,
    dgiStep3SuccessToastAcknowledged: args.dgiStep3SuccessToastAcknowledged,
    caeListLoading: args.caeListLoading,
    hasRequiredOnboardingCaes: args.hasRequiredOnboardingCaes,
    needsCfeImportStep: args.needsCfeImportStep,
    ownerPostCertViewSteps: args.ownerPostCertViewSteps,
    totalSteps: args.totalSteps,
    preCertOffset: args.preCertOffset,
    // These four come off the TENANT, not sessionStorage. They used to be
    // read from a tab-scoped side-channel, which meant closing the tab lost
    // the tenant's place — and a routing change could not take effect for
    // anyone who still had one open, because nothing server-side could clear
    // a stale value.
    //
    // certificatePath stays the in-memory value ALONE. A `?? t.onboardingCertPath`
    // fallback reads well and is wrong: cancelling out of the Fiscalo flow sets
    // the local path to null and PATCHes null without awaiting, so the fallback
    // would find the not-yet-refetched tenant value and pin the user back into
    // the path they just left. useOnboardingTenant hydrates tenant -> local,
    // including nulls, so this direction needs no fallback at all.
    certificatePath: args.certificatePath,
    certificateRequestStatus: args.certificateRequestStatus,
    certAcquisitionDoneMarker: t?.onboardingCertAcquisitionDoneAt != null,
    certWaitContinuedMarker: t?.onboardingCertWaitContinuedAt != null,
    certChoiceMarker: t?.onboardingCertChoice ?? null,
    ownerPersonalizeDoneMarker: t?.onboardingPersonalizeDoneAt != null,
    step3ContinuedMarker: id ? readDgiStep3ContinuedFromSession(id) : false,
    wasLoading: args.wasLoading,
    prevViewStep: args.prevViewStep,
  };
}
